import { INDUSTRIES, MODULES, emailDomain, validateCorporateDomain, type Entitlements, type Industry, type Tenant } from '@titan/shared';
import { SHIPPED_MODULES, defaultEntitlements } from '@titan/modules';
import { X } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router';
import { useSession } from '../../session/SessionProvider.js';
import { ScreenBody, ScreenHeader } from '../../shell/Shell.js';
import { Actions, Button, Card, Field, InlineError, Muted, Select, TextInput, ToggleRow } from '../../ui/index.js';
import { errorMessage } from '../useLoad.js';
import styles from './Admin.module.css';

/**
 * Spec 07 New tenant, trimmed to what is decided: organization, industry, allowed domains
 * and the superadmin. Plan and pricing are set later from the tenant detail; modules
 * appear here only once they have shipped.
 */
export function NewTenant() {
  const { api } = useSession();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [industry, setIndustry] = useState<Industry | ''>('');
  const [modules, setModules] = useState<Entitlements>(defaultEntitlements());
  const [domains, setDomains] = useState<string[]>([]);
  const [domainInput, setDomainInput] = useState('');
  const [domainError, setDomainError] = useState<string | null>(null);
  const [adminEmail, setAdminEmail] = useState('');
  const [adminName, setAdminName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const addDomain = () => {
    const v = validateCorporateDomain(domainInput);
    if (!v.ok) {
      setDomainError(v.message);
      return;
    }
    if (!domains.includes(v.domain)) setDomains([...domains, v.domain]);
    setDomainInput('');
    setDomainError(null);
  };

  const adminDomainOk = adminEmail.includes('@') && domains.includes(emailDomain(adminEmail));
  const canSubmit = name.trim().length >= 2 && industry !== '' && domains.length > 0 && adminName.trim().length > 0 && adminDomainOk && !busy;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      const res = await api<{ tenant: Tenant }>('/admin/tenants', {
        method: 'POST',
        body: JSON.stringify({
          name: name.trim(),
          industry,
          modules,
          allowedDomains: domains,
          superadmin: { email: adminEmail.trim(), name: adminName.trim() },
        }),
      });
      navigate(`/admin/tenants/${res.tenant.id}`, { replace: true });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <ScreenHeader
        eyebrow="administration · new tenant"
        title="New tenant"
        description="Create the organization, set the email domains its people sign in with, and invite the one person who will run it on their side."
        back={{ label: 'Tenants', to: '/admin/tenants' }}
        detail
      />
      <ScreenBody>
        <form onSubmit={submit}>
          <Card variant="form" className={styles.formCard} as="div">
            {error && <InlineError>{error}</InlineError>}
            <Field label="Organization name" htmlFor="name">
              <TextInput id="name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Banco Delta" required />
            </Field>
            <Field label="Industry" htmlFor="industry">
              <Select<Industry> id="industry" options={INDUSTRIES} value={industry} onChange={setIndustry} placeholder="Choose an industry" required />
            </Field>
            <Field
              label="Allowed email domains"
              hint="People sign in and are invited only with addresses on these domains. Corporate domains only; personal providers are refused."
              htmlFor="domain"
            >
              <div className={styles.chipsEdit}>
                {domains.map((d) => (
                  <span key={d} className={styles.domainChip}>
                    {d}
                    <button type="button" className={styles.chipRemove} aria-label={`Remove ${d}`} onClick={() => setDomains(domains.filter((x) => x !== d))}>
                      <X size={12} strokeWidth={1.75} aria-hidden="true" />
                    </button>
                  </span>
                ))}
              </div>
              <div className={styles.inline}>
                <TextInput
                  id="domain"
                  mono
                  value={domainInput}
                  onChange={(e) => setDomainInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addDomain();
                    }
                  }}
                  placeholder="bancodelta.com"
                  invalid={Boolean(domainError)}
                />
                <Button variant="secondary" onClick={addDomain} disabled={!domainInput.trim()}>
                  Add domain
                </Button>
              </div>
              {domainError && <InlineError>{domainError}</InlineError>}
            </Field>
            {SHIPPED_MODULES.length > 0 ? (
              <Field label="Modules to enable" hint="Only DinoCloud changes these later.">
                <div className={styles.toggleList}>
                  {SHIPPED_MODULES.map((k) => (
                    <ToggleRow key={k} label={MODULES[k].label} note={MODULES[k].description} checked={modules[k]} onChange={(on) => setModules({ ...modules, [k]: on })} />
                  ))}
                </div>
              </Field>
            ) : (
              <Muted small>Product modules are enabled from the tenant detail as each one ships. Until then the tenant gets its user management only.</Muted>
            )}
            <Field
              label="Invite their superadmin"
              hint="They receive the invitation and manage their own people from there. DinoCloud keeps entitlements and account links. A backup superadmin can be appointed later."
            >
              <div className={styles.inline}>
                <TextInput type="email" value={adminEmail} onChange={(e) => setAdminEmail(e.target.value)} placeholder="name@bancodelta.com" aria-label="Superadmin email" invalid={adminEmail.length > 0 && !adminDomainOk} />
                <TextInput value={adminName} onChange={(e) => setAdminName(e.target.value)} placeholder="Full name" aria-label="Superadmin name" />
              </div>
              {adminEmail.length > 0 && !adminDomainOk && <InlineError>The superadmin must use one of the allowed domains.</InlineError>}
            </Field>
            <Actions>
              <Button type="submit" disabled={!canSubmit}>
                Create tenant
              </Button>
              <Button variant="secondary" onClick={() => navigate('/admin/tenants')}>
                Cancel
              </Button>
            </Actions>
          </Card>
        </form>
      </ScreenBody>
    </>
  );
}
